import styled from "styled-components";

import type { GeoLocation } from "../api/types";
import { countryName, flagEmoji, locationLabel } from "../lib/geo";
import { Button } from "./primitives";

// The geo opt-in bottom sheet (design "Map consent" artboard). Purely
// presentational: the GeoMap screen owns the mutation and passes state down.

const Sheet = styled.div`
  position: relative;
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.borderHi};
  border-radius: ${({ theme }) => theme.radius.xl};
  padding: ${({ theme }) => `${theme.space(5)} ${theme.space(5)} ${theme.space(4)}`};
  box-shadow: 0 -30px 60px rgba(0, 0, 0, 0.5);
`;

const Grabber = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.15);
  margin: -6px auto 14px;
`;

const Detected = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 14px;
  border-radius: 14px;
  background: ${({ theme }) => theme.color.accentSoft};
  border: 1px solid rgba(0, 152, 234, 0.2);
`;

const PinTile = styled.div`
  position: relative;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surfaceAlt};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;

  &::after {
    content: "";
    position: absolute;
    top: 4px;
    right: 4px;
    width: 6px;
    height: 6px;
    border-radius: 3px;
    background: ${({ theme }) => theme.color.satiety};
    box-shadow: 0 0 6px ${({ theme }) => theme.color.satiety};
  }
`;

const Kicker = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.color.textDim};
`;

const Place = styled.div`
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: ${({ theme }) => theme.color.text};
  margin-top: 1px;
`;

const Title = styled.div`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.6px;
  line-height: 1.15;
  color: ${({ theme }) => theme.color.text};
`;

const Body = styled.p`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.color.textDim};
  margin-top: 8px;
  text-wrap: pretty;
`;

const Rows = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RowWrap = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const Check = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 9px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(63, 207, 142, 0.13);
  color: ${({ theme }) => theme.color.good};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  font-weight: 700;
`;

const RowText = styled.span`
  font-size: 12.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.color.textDim};
  padding-top: 1px;

  b {
    color: ${({ theme }) => theme.color.text};
    font-weight: 600;
  }
`;

const Hint = styled.div`
  margin-top: 12px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  line-height: 1.45;
  color: ${({ theme }) => theme.color.textDim};
`;

const ErrorLine = styled.div`
  margin-top: 12px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  line-height: 1.45;
  color: ${({ theme }) => theme.color.bad};
`;

const Ctas = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;
`;

const NotNow = styled(Button)`
  flex: 1;
`;

const Consent = styled(Button)`
  flex: 1.5;
`;

const Footer = styled.div`
  margin-top: 12px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10px;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.color.textFaint};
`;

function ConsentRow({ children }: { children: React.ReactNode }) {
  return (
    <RowWrap>
      <Check>✓</Check>
      <RowText>{children}</RowText>
    </RowWrap>
  );
}

export function GeoConsentSheet({
  detected,
  pending,
  errorText,
  onConsent,
  onNotNow,
}: {
  /** IP-detected location from GET /geo/status; null when the IP couldn't be resolved. */
  detected: GeoLocation | null;
  pending: boolean;
  errorText: string | null;
  onConsent: () => void;
  onNotNow: () => void;
}) {
  return (
    <Sheet>
      <Grabber />

      {detected && (
        <Detected>
          <PinTile>📍</PinTile>
          <div>
            <Kicker>we think you're in</Kicker>
            <Place>
              {locationLabel(detected)}
              {detected.city ? `, ${countryName(detected.countryCode)}` : ""}{" "}
              {flagEmoji(detected.countryCode)}
            </Place>
          </div>
        </Detected>
      )}

      <Title>show me on the map?</Title>
      <Body>
        Rocky wants to know where his friends are. We can pin you somewhere in your city so others
        can see there's a raccoon-keeper nearby.
      </Body>

      <Rows>
        <ConsentRow>City precision only — never your exact spot.</ConsentRow>
        <ConsentRow>
          Derived from your <b>IP</b>. We don't use GPS or your phone's location.
        </ConsentRow>
        <ConsentRow>Hide yourself again anytime.</ConsentRow>
      </Rows>

      {!detected && (
        <Hint>couldn't detect a location from this connection — try again later</Hint>
      )}
      {errorText && <ErrorLine role="alert">{errorText}</ErrorLine>}

      <Ctas>
        <NotNow type="button" $variant="ghost" onClick={onNotNow}>
          not now
        </NotNow>
        <Consent type="button" disabled={!detected || pending} onClick={onConsent}>
          {!detected
            ? "location unavailable"
            : pending
              ? "pinning…"
              : `pin me in ${locationLabel(detected)}`}
        </Consent>
      </Ctas>

      <Footer>// approximate · ip → city · revocable</Footer>
    </Sheet>
  );
}
